import { useState } from 'react';
import { Button, Dialog, InlineError } from '../../components/ui';
import { useSaveSectionStatuses } from '../projects/hooks';
import { configuredIdsFor, statusesToSave } from './sectionPipeline';
import type { SectionStatusMap } from './sectionPipeline';
import type { ProjectStatus } from '../projects/types';

/**
 * Choosing which status columns one section draws.
 *
 * Only offered while a single section is selected, and only to somebody who
 * can manage the project. Layout is a shared decision — one person narrowing a
 * section changes what everybody sees — so it sits behind the same permission
 * as renaming the section itself.
 *
 * Deliberately NOT a per-person preference. Two people looking at the same
 * board and seeing different columns is a support conversation nobody wants to
 * have, and "where did that column go" has a much better answer when there is
 * exactly one place it could have gone.
 */
export function SectionPipelinePanel({
  projectId,
  sectionId,
  sectionName,
  statuses,
  configured,
}: {
  projectId: string;
  sectionId: string;
  sectionName: string;
  statuses: ProjectStatus[];
  configured: SectionStatusMap;
}) {
  const save = useSaveSectionStatuses(projectId);
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  const active = statuses.filter((status) => status.is_active);

  function start() {
    // Read fresh each time it opens rather than holding state across sections:
    // the same panel is used for whichever tab is selected, and stale ticks
    // from the previous section would be a very quiet way to overwrite the
    // wrong one.
    setChosen(configuredIdsFor(active, configured, sectionId));
    setError('');
    setOpen(true);
  }

  function toggle(statusId: string) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(statusId)) {
        next.delete(statusId);
      } else {
        next.add(statusId);
      }
      return next;
    });
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={start}>
        Columns…
      </Button>
      <Dialog
        open={open}
        title={`Columns for ${sectionName}`}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={save.isPending || chosen.size === 0}
              onClick={() => {
                setError('');
                save.mutate(
                  { sectionId, statusIds: statusesToSave(active, chosen) },
                  {
                    onSuccess: () => setOpen(false),
                    onError: (cause) => setError(cause.message),
                  },
                );
              }}
            >
              {save.isPending ? 'Saving…' : 'Save columns'}
            </Button>
          </>
        }
      >
        <p className="section-pipeline-hint">
          Choose which columns this section shows on the board. Other sections are unaffected, and
          nothing moves — a task keeps its status either way.
        </p>
        {error === '' ? null : <InlineError>{error}</InlineError>}
        <ul className="section-pipeline-list">
          {active.map((status) => (
            <li key={status.id}>
              <label className="section-pipeline-option">
                <input
                  type="checkbox"
                  checked={chosen.has(status.id)}
                  onChange={() => toggle(status.id)}
                />
                <span>{status.name}</span>
              </label>
            </li>
          ))}
        </ul>
        {/* Untick everything and there is no board left, so the save button is
            disabled rather than the choice being silently corrected. Saying
            why beats a button that looks broken. */}
        {chosen.size === 0 ? (
          <p className="section-pipeline-hint">Pick at least one column.</p>
        ) : null}
        {/* The honest note. A task already sitting in an unticked column keeps
            its column until it moves — see columnsForSection. Somebody who
            expects tidying and gets a leftover column should have been told
            here rather than left to wonder. */}
        <p className="section-pipeline-hint">
          A column still holding tasks stays visible until they move, so nothing is hidden by
          narrowing a section.
        </p>
      </Dialog>
    </>
  );
}
